import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { CustomGetProposalQueryData } from "hooks/proposals/useCustomGetProposal";
import useUpdateProposalDiscount from "hooks/proposals/useUpdateProposalDiscount";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { draftColors } from "./draftDesignTokens";

type DraftDiscountDrawerProps = {
  open: boolean;
  onClose: () => void;
  proposal: NonNullable<CustomGetProposalQueryData>;
};

export default function DraftDiscountDrawer({ open, onClose, proposal }: DraftDiscountDrawerProps) {
  const [mutation, { isSuccess }] = useUpdateProposalDiscount();

  const profitCents = +(proposal.profit ?? 0);
  const overallTotalCents = +(proposal.overallTotal ?? 0);
  const existingDiscountCents = +(proposal.discount ?? 0);

  const schema = useMemo(
    () =>
      z
        .object({
          discountType: z.enum(["percent", "amount"]),
          discountValue: z.string(),
        })
        .superRefine((data, ctx) => {
          const numericValue = parseFloat(data.discountValue);
          if (isNaN(numericValue) || numericValue <= 0) return;

          const calculatedDiscountCents =
            data.discountType === "percent"
              ? Math.round((numericValue / 100) * overallTotalCents)
              : Math.round(numericValue * 100);

          if (calculatedDiscountCents > profitCents) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Discount cannot exceed the profit amount",
              path: ["discountValue"],
            });
          }
        }),
    [profitCents, overallTotalCents]
  );

  type DiscountFormValues = z.infer<typeof schema>;

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm<DiscountFormValues>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      discountType: existingDiscountCents > 0 ? "amount" : "percent",
      discountValue: existingDiscountCents > 0 ? (existingDiscountCents / 100).toFixed(2) : "",
    },
  });

  const discountType = watch("discountType");
  const discountValue = watch("discountValue");

  const discountCents = useMemo(() => {
    const numericValue = parseFloat(discountValue);
    if (isNaN(numericValue) || numericValue <= 0) return 0;
    if (discountType === "percent") {
      return Math.round((numericValue / 100) * overallTotalCents);
    }
    return Math.round(numericValue * 100);
  }, [discountType, discountValue, overallTotalCents]);

  const profitAfterDiscount = profitCents - discountCents;
  const proposalTotalAfterDiscount = overallTotalCents - discountCents;

  const onSubmit = useCallback(async () => {
    const result = await mutation({
      variables: { input: { id: proposal.id, discount: discountCents } },
    });
    if (isSuccess(result.data)) {
      onClose();
    }
  }, [mutation, discountCents, proposal.id, isSuccess, onClose]);

  const summaryRows = [
    { label: "Proposal total before discount", value: formatCentsToUSD(overallTotalCents) },
    { label: "Profit amount", value: formatCentsToUSD(profitCents) },
    {
      label: "Discount amount",
      value: formatCentsToUSD(discountCents),
      error: !!errors.discountValue,
    },
    {
      label: "Profit after discount",
      value: formatCentsToUSD(profitAfterDiscount),
      error: profitAfterDiscount < 0,
    },
    { label: "Proposal total after discount", value: formatCentsToUSD(proposalTotalAfterDiscount) },
  ];

  return (
    <UiSideModal open={open} onClose={onClose} title="Edit proposal discount" width="420px">
      <MDBox
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        p="24px"
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <MDBox>
          <UiTypography uiVariant="label" sx={{ mb: "8px" }}>
            Discount type
          </UiTypography>
          <Controller
            name="discountType"
            control={control}
            render={({ field }) => (
              <RadioGroup
                row
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  setValue("discountValue", "");
                }}
                sx={{ gap: "16px" }}
              >
                <FormControlLabel
                  value="percent"
                  control={<Radio size="small" />}
                  label={<UiTypography uiVariant="body-sm">Percent</UiTypography>}
                />
                <FormControlLabel
                  value="amount"
                  control={<Radio size="small" />}
                  label={<UiTypography uiVariant="body-sm">Amount</UiTypography>}
                />
              </RadioGroup>
            )}
          />
        </MDBox>

        <MDBox>
          <UiFieldGroup
            label={discountType === "percent" ? "Discount (%)" : "Discount ($)"}
            value=""
            placeholder="0"
            inputProps={{
              ...register("discountValue"),
              type: "number",
              step: "0.01",
              min: 0,
            }}
          />
          {errors.discountValue && (
            <UiTypography uiVariant="body-sm" sx={{ color: draftColors.red, mt: "4px" }}>
              {errors.discountValue.message}
            </UiTypography>
          )}
        </MDBox>

        <MDBox
          sx={{
            backgroundColor: draftColors.surfaceMuted,
            borderRadius: "8px",
            p: "12px",
          }}
        >
          <Grid container spacing="6px">
            {summaryRows.map((row) => (
              <Grid item xs={12} key={row.label}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <UiTypography uiVariant="body-sm">{row.label}</UiTypography>
                  <UiTypography
                    uiVariant="body-sm"
                    sx={{
                      fontWeight: 500,
                      color: row.error ? draftColors.red : draftColors.textPrimary,
                    }}
                  >
                    {row.value}
                  </UiTypography>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </MDBox>

        <MDBox display="flex" justifyContent="flex-end">
          <UiButton uiVariant="primary-green" type="submit" disabled={!!errors.discountValue}>
            Save
          </UiButton>
        </MDBox>
      </MDBox>
    </UiSideModal>
  );
}
